/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {responsiveHelper} from '@amblique/base/app/helpers/responsive-helper';

/**
 * Centralized media query registry for performance optimization.
 * Lazily creates MediaQueryList instances only for breakpoints that are actually used.
 * Automatically cleans up unused media queries when subscribers unsubscribe.
 *
 * This singleton manages:
 * - MediaQueryList instances (created on-demand)
 * - Reference counting for automatic cleanup
 * - Current breakpoint state for global queries
 * - Subscriber notifications on breakpoint changes
 *
 * @example Basic usage
 * const currentBreakpoint = mediaQueryRegistry.getCurrentBreakpoint();
 * // => 'base', 'sm', 'md', 'lg', 'xl', or '2xl'
 *
 * @example Subscribe to responsive value changes (device keys)
 * const responsiveObj = { mobile: 'mobile-id', tablet: 'tablet-id', desktop: 'desktop-id' };
 * const unsubscribe = mediaQueryRegistry.subscribe(
 *   ({ breakpoint, value }) => {
 *     console.log(`Breakpoint: ${breakpoint}, Value: ${value}`);
 *   },
 *   responsiveObj
 * );
 * // Later: unsubscribe();
 *
 * @example Subscribe to responsive value changes (breakpoint keys)
 * const responsiveObj = { base: 'mobile-id', md: 'tablet-id', xl: 'desktop-id' };
 * const unsubscribe = mediaQueryRegistry.subscribe(
 *   ({ breakpoint, value }) => {
 *     console.log(`Breakpoint: ${breakpoint}, Value: ${value}`);
 *   },
 *   responsiveObj
 * );
 *
 * @example Get current matched value
 * const value = mediaQueryRegistry.getMatchedValue(responsiveObj);
 * // => Returns value for current breakpoint with proper cascade
 * // Supports both device keys ({ mobile, tablet, desktop }) and breakpoint keys ({ base, md, xl })
 */
export const mediaQueryRegistry = {
  // ============================================================================
  // Protected Properties
  // ============================================================================

  /**
   * Registry of media queries with reference counting
   * Structure: { breakpoint: { mql: MediaQueryList, refCount: number } }
   * @protected
   * @type {Object.<string, {mql: MediaQueryList, refCount: number}>}
   */
  queries: {},

  /**
   * Map of listeners to their configuration
   * Structure: Map(callback -> { responsiveObj, breakpoints[] })
   * @protected
   * @type {Map<Function, {responsiveObj: object, breakpoints: string[]}>}
   */
  listeners: new Map(),

  /**
   * Current active breakpoint name
   * @protected
   * @type {string|null}
   */
  currentBreakpoint: null,

  /**
   * Initialization flag for global breakpoint tracking
   * @protected
   * @type {boolean}
   */
  initialized: false,

  // ============================================================================
  // Public Methods
  // ============================================================================

  /**
   * Get the current matched breakpoint name.
   * Automatically initializes global tracking on first call by creating
   * MediaQueryList for all breakpoints.
   *
   * @returns {string} Current breakpoint ('base', 'sm', 'md', 'lg', 'xl', or '2xl')
   */
  getCurrentBreakpoint() {
    if (typeof window === 'undefined') {
      return 'base';
    }

    // Initialize on first call
    if (!this.initialized) {
      // Get all breakpoints from responsiveHelper (excludes 'base')
      const breakpoints = responsiveHelper.breakpoints.filter((key) => key !== 'base');

      breakpoints.forEach((breakpoint) => {
        this.ensureMediaQuery(breakpoint);
      });

      this.initialized = true;
      this.updateCurrentBreakpoint();
    }

    return this.currentBreakpoint || 'base';
  },

  /**
   * Get the current value from a responsive object based on breakpoint.
   * Implements proper breakpoint cascade (falls back to smaller breakpoints if no match).
   * Supports both device keys (mobile, tablet, desktop) and breakpoint keys (base, sm, md, etc.).
   *
   * @param {object} responsiveObj - Responsive object with device or breakpoint keys
   * @param {string} [breakpoint] - Optional breakpoint name, device, or media query. If not provided, uses getCurrentBreakpoint()
   * @returns {*} Matched value for breakpoint, or null if no match
   *
   * @example With automatic breakpoint detection (device keys)
   * const responsiveObj = { mobile: 'mobile-id', tablet: 'tablet-id', desktop: 'desktop-id' };
   * const value = mediaQueryRegistry.getMatchedValue(responsiveObj);
   * // At 'lg' breakpoint => returns 'tablet-id' (cascades from 'md')
   *
   * @example With breakpoint keys
   * const responsiveObj = { base: 'mobile-id', md: 'tablet-id', xl: 'desktop-id' };
   * const value = mediaQueryRegistry.getMatchedValue(responsiveObj);
   * // At 'lg' breakpoint => returns 'tablet-id' (cascades from 'md')
   *
   * @example With explicit breakpoint
   * const value = mediaQueryRegistry.getMatchedValue(responsiveObj, 'md');
   * // => returns 'tablet-id'
   */
  getMatchedValue(responsiveObj, breakpoint) {
    if (typeof window === 'undefined') {
      // SSR: normalize and return base/mobile/default value
      const normalized = this.normalizeResponsiveObj(responsiveObj);

      return normalized.base || responsiveObj.mobile || responsiveObj.value || null;
    }

    // Normalize responsive object (convert device keys to breakpoint keys)
    const normalized = this.normalizeResponsiveObj(responsiveObj);

    // Use provided breakpoint or get current
    let currentBreakpoint = breakpoint;

    if (!currentBreakpoint) {
      currentBreakpoint = this.getCurrentBreakpoint();
    } else {
      // Normalize: device name -> breakpoint name
      const deviceMap = responsiveHelper.deviceToBreakpointMap;

      if (deviceMap[currentBreakpoint]) {
        currentBreakpoint = deviceMap[currentBreakpoint];
      }
    }

    // Try exact match first
    if (normalized[currentBreakpoint] !== undefined) {
      return normalized[currentBreakpoint];
    }

    // Fallback through breakpoint cascade (desktop-first)
    const breakpointOrder = responsiveHelper.reversedBreakpoints;
    const currentIndex = breakpointOrder.indexOf(currentBreakpoint);

    for (let i = currentIndex + 1; i < breakpointOrder.length; i++) {
      const fallbackBreakpoint = breakpointOrder[i];

      if (normalized[fallbackBreakpoint] !== undefined) {
        return normalized[fallbackBreakpoint];
      }
    }

    // Final fallback to default value
    return responsiveObj.value || null;
  },

  /**
   * Subscribe to responsive value changes.
   * Creates MediaQueryList only for breakpoints present in responsiveObj.
   * Automatically cleans up media queries when last subscriber unsubscribes.
   * Supports both device keys (mobile, tablet, desktop) and breakpoint keys (base, sm, md, etc.).
   *
   * @param {Function} callback - Function called on change, receives {breakpoint, value}
   * @param {object} responsiveObj - Responsive object with device or breakpoint keys
   * @returns {Function} Unsubscribe function
   *
   * @example With device keys
   * const responsiveObj = { mobile: 'id1', tablet: 'id2', desktop: 'id3' };
   * const unsubscribe = mediaQueryRegistry.subscribe(
   *   ({ breakpoint, value }) => {
   *     console.log(`Now at ${breakpoint}: ${value}`);
   *   },
   *   responsiveObj
   * );
   *
   * @example With breakpoint keys
   * const responsiveObj = { base: 'id1', md: 'id2', xl: 'id3' };
   * const unsubscribe = mediaQueryRegistry.subscribe(
   *   ({ breakpoint, value }) => {
   *     console.log(`Now at ${breakpoint}: ${value}`);
   *   },
   *   responsiveObj
   * );
   *
   * // Later, when done:
   * unsubscribe();
   */
  subscribe(callback, responsiveObj) {
    if (typeof window === 'undefined') {
      return () => {}; // No-op for SSR
    }

    const breakpoints = this.extractBreakpoints(responsiveObj);

    breakpoints.forEach((breakpoint) => {
      this.ensureMediaQuery(breakpoint);
    });

    // Store listener configuration with original responsiveObj (for getMatchedValue)
    this.listeners.set(callback, {responsiveObj, breakpoints});

    // Return unsubscribe function
    return () => {
      const config = this.listeners.get(callback);

      if (!config) {
        return;
      }

      this.listeners.delete(callback);

      config.breakpoints.forEach((breakpoint) => {
        this.decrementRefCount(breakpoint);
      });
    };
  },

  /**
   * Cleanup all media query listeners and reset state.
   * Useful for testing, hot module replacement, or manual cleanup.
   *
   * @returns {void}
   */
  cleanup() {
    // Remove all media query listeners
    Object.values(this.queries).forEach(({mql}) => {
      if (mql) {
        mql.removeEventListener('change', () => this.onBreakpointChange());
      }
    });

    this.queries = {};
    this.listeners.clear();
    this.currentBreakpoint = null;
    this.initialized = false;
  },

  // ============================================================================
  // Protected Methods
  // ============================================================================

  /**
   * Normalize responsive object by converting device keys to breakpoint keys.
   * Supports both device names (mobile, tablet, desktop) and breakpoint names (base, sm, md, etc.).
   * Handles mixed formats and merges values when multiple devices map to the same breakpoint.
   *
   * @protected
   * @param {object} responsiveObj - Responsive object with device or breakpoint keys
   * @returns {object} Normalized object with only breakpoint keys
   *
   * @example
   * normalizeResponsiveObj({ mobile: 'id1', tablet: 'id2', desktop: 'id3' })
   * // => { base: 'id1', md: 'id2', xl: 'id3' }
   *
   * @example Mixed format
   * normalizeResponsiveObj({ mobile: 'id1', md: 'id2', desktop: 'id3' })
   * // => { base: 'id1', md: 'id2', xl: 'id3' }
   */
  normalizeResponsiveObj(responsiveObj) {
    if (!responsiveObj || typeof responsiveObj !== 'object') {
      return {};
    }

    const normalized = {};
    const deviceMap = responsiveHelper.deviceToBreakpointMap;
    const metadataKeys = ['isResponsive', 'isEmpty', 'breakpoints', 'value'];

    for (const [key, value] of Object.entries(responsiveObj)) {
      // Skip metadata keys
      if (metadataKeys.includes(key)) {
        continue;
      }

      // Check if key is a valid breakpoint name
      if (responsiveHelper.breakpoints.includes(key)) {
        normalized[key] = value;
        continue;
      }

      // Check if key is a device name, convert to breakpoint
      if (deviceMap[key]) {
        const breakpoint = deviceMap[key];

        // Merge if breakpoint already has a value (e.g., mixed device + breakpoint keys)
        if (normalized[breakpoint] !== undefined) {
          // For objects, merge deeply; for primitives, device value takes precedence
          if (typeof value === 'object' && value !== null && !Array.isArray(value) &&
            typeof normalized[breakpoint] === 'object' && normalized[breakpoint] !== null && !Array.isArray(normalized[breakpoint])) {
            normalized[breakpoint] = {...normalized[breakpoint], ...value};
          } else {
            normalized[breakpoint] = value;
          }
        } else {
          normalized[breakpoint] = value;
        }
      }
    }

    return normalized;
  },

  /**
   * Extract and normalize breakpoint names from responsive object.
   * Converts device names to breakpoint names for media query creation.
   * Excludes 'base' as it doesn't need a media query.
   *
   * @protected
   * @param {object} responsiveObj - Responsive object with device or breakpoint keys
   * @returns {Array<string>} Array of normalized breakpoint names (excluding 'base')
   */
  extractBreakpoints(responsiveObj) {
    const normalized = this.normalizeResponsiveObj(responsiveObj);

    return Object.keys(normalized).filter((key) => key !== 'base');
  },

  /**
   * Ensure MediaQueryList exists for a breakpoint.
   * Creates it if it doesn't exist, increments ref count if it does.
   *
   * @protected
   * @param {string} breakpoint - Breakpoint name
   * @returns {void}
   */
  ensureMediaQuery(breakpoint) {
    // Skip 'base' as it doesn't need a media query
    if (breakpoint === 'base') {
      return;
    }

    // If query already exists, increment ref count
    if (this.queries[breakpoint]) {
      this.queries[breakpoint].refCount++;

      return;
    }

    // Get breakpoint value from responsiveHelper (preserves original unit)
    const breakpointValue = responsiveHelper.getBreakpointValue(breakpoint);

    if (!breakpointValue) {
      console.warn(`[mediaQueryRegistry] Unknown breakpoint: ${breakpoint}`);

      return;
    }

    // Use original unit (em, rem, px, etc.) for better accessibility
    const query = `(min-width: ${breakpointValue})`;
    const mql = window.matchMedia(query);

    // Create bound handler function
    const handler = () => this.onBreakpointChange();

    // Add change listener
    mql.addEventListener('change', handler);

    // Store with ref count and handler
    this.queries[breakpoint] = {
      mql,
      handler,
      refCount: 1,
    };
  },

  /**
   * Decrement reference count and remove MediaQueryList if no longer needed.
   *
   * @protected
   * @param {string} breakpoint - Breakpoint name
   * @returns {void}
   */
  decrementRefCount(breakpoint) {
    const query = this.queries[breakpoint];

    if (!query) {
      return;
    }

    query.refCount--;

    // Remove if no more subscribers (but keep if initialized for global tracking)
    if (query.refCount === 0 && !this.initialized) {
      query.mql.removeEventListener('change', query.handler);
      delete this.queries[breakpoint];
    }
  },

  /**
   * Update the current breakpoint based on media query matches.
   * Finds the highest matching breakpoint.
   *
   * @protected
   * @returns {void}
   */
  updateCurrentBreakpoint() {
    // Find highest matching breakpoint (desktop-first)
    // Use responsiveHelper's sorted breakpoints (reversed for desktop-first)
    const breakpointOrder = [...responsiveHelper.breakpoints].reverse();

    for (const name of breakpointOrder) {
      // Skip 'base' - it doesn't have a media query
      if (name === 'base') {
        continue;
      }

      if (this.queries[name]?.mql.matches) {
        this.currentBreakpoint = name;

        return;
      }
    }

    this.currentBreakpoint = 'base';
  },

  /**
   * Handle media query change event.
   * Updates current breakpoint and notifies subscribers.
   *
   * @protected
   * @returns {void}
   */
  onBreakpointChange() {
    const previousBreakpoint = this.currentBreakpoint;

    this.updateCurrentBreakpoint();

    // If breakpoint didn't actually change, no need to notify
    if (previousBreakpoint === this.currentBreakpoint) {
      return;
    }

    // Notify all listeners
    this.listeners.forEach((config, callback) => {
      const {responsiveObj} = config;

      // Get new value for current breakpoint (with cascading fallback)
      const value = this.getMatchedValue(responsiveObj);

      // Call listener with both breakpoint and value
      callback({
        breakpoint: this.currentBreakpoint,
        value,
      });
    });
  },
};
